import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Intelligence.css';

const Intelligence = ({ apiBase }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useSampleData, setUseSampleData] = useState(false);

  useEffect(() => {
    loadData();
  }, [apiBase]);

  // No fabricated fallback — real data comes from /api/market-analysis. Empty structure = honest "no data yet" state.
  const getSampleData = () => {
    return {
      analysis: {},
      competitors: [],
      amenities: []
    };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading intelligence data from:', `${apiBase}/market-analysis`);
      // Get all market analyses
      const res = await axios.get(`${apiBase}/market-analysis`);
      console.log('Market analysis response:', res.data);
      
      if (!res.data) {
        setError('No response from API');
        return;
      }
      
      // Handle array response
      if (Array.isArray(res.data) && res.data.length > 0) {
        // Get full details of first one (Jogeshwari)
        const analysisId = res.data[0].id;
        console.log('Fetching full analysis for ID:', analysisId);
        const fullRes = await axios.get(`${apiBase}/market-analysis/${analysisId}`);
        console.log('Full market analysis data:', fullRes.data);
        
        if (fullRes.data && fullRes.data.analysis) {
          setData(fullRes.data);
          setUseSampleData(false);
        } else {
          console.error('Invalid data structure:', fullRes.data);
          // Fallback to sample data
          setUseSampleData(true);
          setData(getSampleData());
        }
      } else if (res.data && !Array.isArray(res.data) && res.data.id) {
        // Handle case where API returns single object
        console.log('Single analysis object received');
        const fullRes = await axios.get(`${apiBase}/market-analysis/${res.data.id}`);
        if (fullRes.data && fullRes.data.analysis) {
          setData(fullRes.data);
          setUseSampleData(false);
        } else {
          setUseSampleData(true);
          setData(getSampleData());
        }
      } else {
        // No data, use sample
        console.log('No market analysis data, using sample data');
        setUseSampleData(true);
        setData(getSampleData());
      }
    } catch (err) {
      console.error('Error loading intelligence data:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      console.error('Error details:', errorMsg);
      
      // On any error, use sample data as fallback
      console.log('API error, using sample data as fallback');
      const sampleData = getSampleData();
      setUseSampleData(true);
      setData(sampleData);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0';
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(amount);
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    const num = parseFloat(price);
    if (num >= 10000000) {
      return `₹${(num / 10000000).toFixed(2)} Cr`;
    } else if (num >= 100000) {
      return `₹${(num / 100000).toFixed(2)} Lac`;
    }
    return `₹${formatCurrency(num)}`;
  };

  if (loading) {
    return (
      <div className="intelligence-container">
        <div className="intelligence-header">
          <div className="header-content">
            <h1 className="intelligence-title">
              <span className="title-icon">🧠</span>
              The Intelligence
            </h1>
            <p className="intelligence-subtitle">
              Comprehensive Micro Market Analysis & Competitive Intelligence
            </p>
          </div>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading Intelligence Data...</p>
        </div>
      </div>
    );
  }

  // If we have an error but no data, show error with option to use sample data
  if (error && !data) {
    return (
      <div className="intelligence-container">
        <div className="intelligence-header">
          <div className="header-content">
            <h1 className="intelligence-title">
              <span className="title-icon">🧠</span>
              The Intelligence
            </h1>
            <p className="intelligence-subtitle">
              Comprehensive Micro Market Analysis & Competitive Intelligence
            </p>
          </div>
        </div>
        <div className="error-state">
          <h2>⚠️ {error}</h2>
          <p style={{ marginTop: '16px', color: '#64748b', marginBottom: '24px' }}>
            Would you like to view sample data instead?
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button onClick={loadData} className="retry-btn">
              Retry API
            </button>
            <button 
              onClick={() => {
                setData(getSampleData());
                setUseSampleData(true);
                setError(null);
              }}
              className="retry-btn"
              style={{ background: '#10b981' }}
            >
              Use Sample Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If no data at all, show loading or use sample data
  if (!data || !data.analysis) {
    // If we're not loading and have no data, use sample data
    if (!loading && !error) {
      // This will be handled by useEffect or we show sample directly
      return (
        <div className="intelligence-container">
          <div className="intelligence-header">
            <div className="header-content">
              <h1 className="intelligence-title">
                <span className="title-icon">🧠</span>
                The Intelligence
              </h1>
              <p className="intelligence-subtitle">
                Comprehensive Micro Market Analysis & Competitive Intelligence
              </p>
            </div>
          </div>
          <div className="error-state">
            <h2>⚠️ No Data Available</h2>
            <p style={{ marginTop: '16px', color: '#64748b', marginBottom: '24px' }}>
              Click below to load sample data or retry the API.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={loadData} className="retry-btn">
                Retry API
              </button>
              <button 
                onClick={() => {
                  const sampleData = getSampleData();
                  setData(sampleData);
                  setUseSampleData(true);
                  setError(null);
                }}
                className="retry-btn"
                style={{ background: '#10b981' }}
              >
                Load Sample Data
              </button>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const analysis = data.analysis;
  const competitors = data.competitors || [];
  const amenities = data.amenities || [];

  // Group amenities by type
  const amenitiesByType = amenities.reduce((acc, amenity) => {
    const type = amenity.amenity_type || 'Other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(amenity);
    return acc;
  }, {});

  // Filter specific amenity types
  const schools = amenities.filter(a => 
    a.amenity_type && a.amenity_type.toLowerCase().includes('school')
  );
  const colleges = amenities.filter(a => 
    a.amenity_type && a.amenity_type.toLowerCase().includes('college')
  );
  const airports = amenities.filter(a => 
    a.amenity_type && a.amenity_type.toLowerCase().includes('airport')
  );
  const offices = amenities.filter(a => 
    a.amenity_type && (a.amenity_type.toLowerCase().includes('office') || 
                       a.amenity_type.toLowerCase().includes('cbd') ||
                       a.amenity_type.toLowerCase().includes('business'))
  );

  // Ensure we have REAL data before rendering main content (empty {} => show no-data state)
  if (!analysis || Object.keys(analysis).length === 0) {
    return (
      <div className="intelligence-container">
        <div className="intelligence-header">
          <div className="header-content">
            <h1 className="intelligence-title">
              <span className="title-icon">🧠</span>
              The Intelligence
            </h1>
            <p className="intelligence-subtitle">
              Comprehensive Micro Market Analysis & Competitive Intelligence
            </p>
          </div>
        </div>
        <div className="error-state">
          <h2>⚠️ No Data Available</h2>
          <p style={{ marginTop: '16px', color: '#64748b', marginBottom: '16px' }}>
            Market analysis data is required to display intelligence insights.
          </p>
          <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>
            Create a market analysis for this project to populate competitive intelligence here.
          </p>
          <button onClick={loadData} className="retry-btn" style={{ marginTop: '24px' }}>
            Retry Loading Data
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="intelligence-container">
      {/* Header */}
      <div className="intelligence-header">
        <div className="header-content">
          <h1 className="intelligence-title">
            <span className="title-icon">🧠</span>
            The Intelligence
          </h1>
          <p className="intelligence-subtitle">
            Comprehensive Micro Market Analysis & Competitive Intelligence
          </p>
        </div>
        <div className="location-badge">
          📍 {analysis.location || 'Location not set'}
        </div>
      </div>

      {/* Market Overview Cards */}
      <div className="overview-grid">
        <div className="overview-card primary">
          <div className="card-icon">🏗️</div>
          <div className="card-content">
            <div className="card-label">Total Projects</div>
            <div className="card-value">{analysis.total_projects || 0}</div>
            <div className="card-sublabel">by {analysis.total_builders || 0} builders</div>
          </div>
        </div>
        <div className="overview-card success">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <div className="card-label">Sales Volume</div>
            <div className="card-value">₹{formatCurrency((analysis.sales_volume_crores || 0) * 10000000)} Cr</div>
            <div className="card-sublabel">2024-25</div>
          </div>
        </div>
        <div className="overview-card warning">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <div className="card-label">Price Growth</div>
            <div className="card-value">{analysis.price_appreciation_3yr || 0}%</div>
            <div className="card-sublabel">3-year appreciation</div>
          </div>
        </div>
        <div className="overview-card info">
          <div className="card-icon">🏠</div>
          <div className="card-content">
            <div className="card-label">Avg Price/sq.ft</div>
            <div className="card-value">₹{formatCurrency(analysis.avg_price_per_sqft || 0)}</div>
            <div className="card-sublabel">Current market rate</div>
          </div>
        </div>
        <div className="overview-card secondary">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <div className="card-label">Rental Yield</div>
            <div className="card-value">{analysis.rental_yield || 0}%</div>
            <div className="card-sublabel">Annual yield</div>
          </div>
        </div>
        <div className="overview-card rating">
          <div className="card-icon">⭐</div>
          <div className="card-content">
            <div className="card-label">Market Rating</div>
            <div className="card-value">{analysis.market_rating || 5}/5</div>
            <div className="card-sublabel">Market strength</div>
          </div>
        </div>
      </div>

      {/* Competition Analysis Section */}
      <div className="section">
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-icon">🏢</span>
            Competition Analysis
          </h2>
          <div className="section-badge">{competitors.length} Competitor Projects</div>
        </div>
        {analysis.competition_analysis && (
          <div className="info-box">
            <p>{analysis.competition_analysis}</p>
          </div>
        )}
        
        {competitors.length > 0 ? (
          <div className="table-wrapper">
            <table className="intelligence-table">
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Developer</th>
                  <th>Configuration</th>
                  <th>Area (sq.ft)</th>
                  <th>Price Range</th>
                  <th>Price/sq.ft</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((project, idx) => (
                  <tr key={project.id || idx}>
                    <td><strong>{project.project_name || 'N/A'}</strong></td>
                    <td>{project.developer_name || 'N/A'}</td>
                    <td>{project.configuration || 'N/A'}</td>
                    <td>
                      {project.carpet_area_min && project.carpet_area_max
                        ? `${project.carpet_area_min}-${project.carpet_area_max}`
                        : project.carpet_area_min || project.carpet_area_max || 'N/A'}
                    </td>
                    <td>
                      {project.price_min && project.price_max
                        ? `${formatPrice(project.price_min)} - ${formatPrice(project.price_max)}`
                        : project.price_min
                        ? formatPrice(project.price_min)
                        : 'N/A'}
                    </td>
                    <td>
                      {project.price_per_sqft
                        ? `₹${formatCurrency(project.price_per_sqft)}`
                        : 'N/A'}
                    </td>
                    <td>
                      <span className={`status-badge ${(project.possession_status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                        {project.possession_status || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <p>No competitor data available</p>
          </div>
        )}
      </div>

      {/* Distances to Key Locations */}
      <div className="section">
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-icon">📍</span>
            Distances to Key Locations
          </h2>
        </div>
        
        <div className="distances-grid">
          {/* Schools */}
          {schools.length > 0 && (
            <div className="distance-category">
              <div className="category-header">
                <span className="category-icon">🏫</span>
                <h3>Schools</h3>
              </div>
              <div className="distance-list">
                {schools.slice(0, 5).map((school, idx) => (
                  <div key={idx} className="distance-item">
                    <div className="distance-name">{school.amenity_name}</div>
                    <div className="distance-value">
                      {school.distance_km ? `${school.distance_km} km` : 'N/A'}
                      {school.travel_time_minutes && (
                        <span className="travel-time"> ({school.travel_time_minutes} min)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Colleges */}
          {colleges.length > 0 && (
            <div className="distance-category">
              <div className="category-header">
                <span className="category-icon">🎓</span>
                <h3>Colleges</h3>
              </div>
              <div className="distance-list">
                {colleges.slice(0, 5).map((college, idx) => (
                  <div key={idx} className="distance-item">
                    <div className="distance-name">{college.amenity_name}</div>
                    <div className="distance-value">
                      {college.distance_km ? `${college.distance_km} km` : 'N/A'}
                      {college.travel_time_minutes && (
                        <span className="travel-time"> ({college.travel_time_minutes} min)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Airport */}
          {airports.length > 0 && (
            <div className="distance-category">
              <div className="category-header">
                <span className="category-icon">✈️</span>
                <h3>Airport</h3>
              </div>
              <div className="distance-list">
                {airports.slice(0, 3).map((airport, idx) => (
                  <div key={idx} className="distance-item">
                    <div className="distance-name">{airport.amenity_name}</div>
                    <div className="distance-value">
                      {airport.distance_km ? `${airport.distance_km} km` : 'N/A'}
                      {airport.travel_time_minutes && (
                        <span className="travel-time"> ({airport.travel_time_minutes} min)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Office Complexes */}
          {offices.length > 0 && (
            <div className="distance-category">
              <div className="category-header">
                <span className="category-icon">🏢</span>
                <h3>Office Complexes</h3>
              </div>
              <div className="distance-list">
                {offices.slice(0, 5).map((office, idx) => (
                  <div key={idx} className="distance-item">
                    <div className="distance-name">{office.amenity_name}</div>
                    <div className="distance-value">
                      {office.distance_km ? `${office.distance_km} km` : 'N/A'}
                      {office.travel_time_minutes && (
                        <span className="travel-time"> ({office.travel_time_minutes} min)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* All Amenities Table */}
        {amenities.length > 0 && (
          <div className="amenities-table-section">
            <h3 className="subsection-title">All Amenities</h3>
            <div className="table-wrapper">
              <table className="intelligence-table compact">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Distance</th>
                    <th>Travel Time</th>
                  </tr>
                </thead>
                <tbody>
                  {amenities.map((amenity, idx) => (
                    <tr key={idx}>
                      <td>
                        <span className="amenity-type-badge">{amenity.amenity_type || 'Other'}</span>
                      </td>
                      <td>{amenity.amenity_name || 'N/A'}</td>
                      <td>{amenity.distance_km ? `${amenity.distance_km} km` : 'N/A'}</td>
                      <td>{amenity.travel_time_minutes ? `${amenity.travel_time_minutes} min` : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Additional Market Insights */}
      {(analysis.infrastructure_summary || analysis.swot_analysis || analysis.recommendations) && (
        <div className="section">
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-icon">💡</span>
              Market Insights
            </h2>
          </div>
          
          {analysis.infrastructure_summary && (
            <div className="insight-box">
              <h3 className="insight-title">Infrastructure Summary</h3>
              <p>{analysis.infrastructure_summary}</p>
            </div>
          )}

          {analysis.swot_analysis && (
            <div className="insight-box">
              <h3 className="insight-title">SWOT Analysis</h3>
              <p>{analysis.swot_analysis}</p>
            </div>
          )}

          {analysis.recommendations && (
            <div className="insight-box highlight">
              <h3 className="insight-title">Recommendations</h3>
              <p>{analysis.recommendations}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Intelligence;

